import React, { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

// Mock conversation data with more detailed AI responses
const initialConversation = [
    {
        id: 1,
        sender: 'ai',
        message: 'Hello! How can I help with your crypto portfolio today?',
        timestamp: new Date(Date.now() - 60000 * 5).toISOString()
    },
    {
        id: 2,
        sender: 'user',
        message: 'What are your thoughts on Bitcoin right now?',
        timestamp: new Date(Date.now() - 60000 * 4).toISOString()
    },
    {
        id: 3,
        sender: 'ai',
        message: 'Bitcoin is showing strong momentum with key metrics suggesting accumulation by long-term holders. Technical indicators point to support at $62K with resistance at $65.8K. Recent demand from spot ETFs has been notably high. Would you like me to suggest an entry strategy based on your risk profile?',
        timestamp: new Date(Date.now() - 60000 * 3).toISOString()
    },
];

const suggestedQueries = [
    'How is my portfolio performing?',
    'What should I buy today?',
    'Which asset should I take profits on?',
    'Show me market sentiment analysis',
];

// Map of predefined answers for specific questions
const predefinedAnswers: Record<string, string> = {
    'How is my portfolio performing?': "Your portfolio is up 3.2% today, outperforming the broader market by 1.7%. Your Ethereum holdings contributed most to this gain at +5.3%, while your Cardano position is down 0.8%. Overall, your 30-day performance shows a positive trend with 8.5% growth. Would you like a detailed breakdown by asset?",

    'What should I buy today?': "Based on current market conditions and your existing portfolio composition, I'd recommend considering Avalanche (AVAX). It's showing strong fundamentals with increased developer activity and recent protocol upgrades. It would also improve your portfolio diversification. The current price of $32.14 represents a potential entry point with strong support at $30.50. Would you like more details on this recommendation?",

    'Which asset should I take profits on?': "Looking at your portfolio, Solana (SOL) has experienced a significant run-up of 42% over the past month and is showing some overbought signals on the 4-hour chart. You might consider taking partial profits on 15-20% of your position. The RSI indicator at 78 suggests potential for a short-term correction, which could offer a re-entry opportunity at a lower price.",

    'Show me market sentiment analysis': "Current market sentiment is cautiously bullish with a fear & greed index score of 65 (Greed). On-chain metrics show accumulation from long-term holders while short-term holders are taking profits. Social media sentiment analysis indicates a 20% increase in positive mentions of Bitcoin over the past week. Institutional flows remain positive with $267M net inflows to crypto ETFs in the last 7 days. Would you like me to focus on any particular aspect of sentiment?",
};

const AIAgentDemo: React.FC = () => {
    const [conversation, setConversation] = useState(initialConversation);
    const [currentMessage, setCurrentMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [currentTypingMessage, setCurrentTypingMessage] = useState('');
    const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const sectionRef = useRef<HTMLElement>(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"],
    });

    // Animation values based on scroll
    const blurRadius = useTransform(scrollYProgress, [0.1, 0.5], [10, 0]);
    const messageContainerRef = useRef<HTMLDivElement>(null);

    // Auto-scroll effect for messages
    useEffect(() => {
        if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        }
    }, [conversation, currentTypingMessage]);

    // Cleanup function for typing interval
    useEffect(() => {
        return () => {
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
                typingIntervalRef.current = null;
            }
        };
    }, []);

    // Simulated AI response
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!currentMessage.trim()) return;

        // Clear any existing interval
        if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
        }

        // Add user message
        const userMessage = {
            id: conversation.length + 1,
            sender: 'user',
            message: currentMessage.trim(),
            timestamp: new Date().toISOString(),
        };

        setConversation([...conversation, userMessage]);
        setCurrentMessage('');

        // Simulate AI typing
        setIsTyping(true);

        // Get predefined answer if available, otherwise generate generic response
        let aiResponse: string;
        if (predefinedAnswers[userMessage.message]) {
            aiResponse = predefinedAnswers[userMessage.message];
        } else {
            aiResponse = getGenericResponse(userMessage.message);
        }

        // Generate a typing animation
        let displayText = '';
        let i = 0;
        typingIntervalRef.current = setInterval(() => {
            if (i < aiResponse.length) {
                displayText += aiResponse.charAt(i);
                setCurrentTypingMessage(displayText);
                i++;
            } else {
                if (typingIntervalRef.current) {
                    clearInterval(typingIntervalRef.current);
                    typingIntervalRef.current = null;
                }
                setIsTyping(false);
                setCurrentTypingMessage('');

                // Add AI response to conversation
                const aiMessage = {
                    id: conversation.length + 2,
                    sender: 'ai',
                    message: aiResponse,
                    timestamp: new Date().toISOString(),
                };

                setConversation(prev => [...prev, aiMessage]);
            }
        }, 15); // Speed of typing animation
    };

    // Generate a generic response for unknown queries
    const getGenericResponse = (message: string) => {
        // Check for keywords to give somewhat relevant responses
        const lowerMessage = message.toLowerCase();

        if (lowerMessage.includes('bitcoin') || lowerMessage.includes('btc')) {
            return "Bitcoin's recent price action shows consolidation above key support levels. On-chain metrics indicate continued accumulation from large entities, while exchange reserves are declining. This suggests a potential continuation of the uptrend if global market conditions remain stable.";
        }

        if (lowerMessage.includes('ethereum') || lowerMessage.includes('eth')) {
            return "Ethereum is showing strength following its successful network upgrades. The shift to proof-of-stake has reduced new issuance by approximately 90%, creating deflationary pressure during periods of high network activity. The next major catalyst could be the approval of spot Ethereum ETFs, which analysts project might happen in Q3.";
        }

        if (lowerMessage.includes('risk') || lowerMessage.includes('safe') || lowerMessage.includes('protect')) {
            return "To manage risk effectively in your crypto portfolio, I'd recommend implementing position sizing based on volatility, setting stop-losses at key technical levels, and ensuring no single asset represents more than 20% of your portfolio. Additionally, maintaining a cash reserve of 15-25% allows you to capitalize on market corrections.";
        }

        if (lowerMessage.includes('sell') || lowerMessage.includes('exit') || lowerMessage.includes('profit')) {
            return "When considering taking profits, look at both technical indicators and your personal investment goals. A strategy many successful investors use is scaling out gradually - selling 20-30% of a position when it reaches your initial target, another portion at a higher target, and keeping some for potential further upside.";
        }

        // Default response if no keywords match
        return "Based on your portfolio and market conditions, I'd recommend a balanced approach. Current market sentiment indicates cautious optimism, with institutional interest growing steadily. Would you like me to analyze any specific aspect of your portfolio or the broader market trends?";
    };

    // Select a suggested query
    const handleSuggestedQuery = (query: string) => {
        setCurrentMessage(query);
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <motion.section
            ref={sectionRef}
            id="ai-assistant"
            className="section bg-primary py-20 px-6 md:px-12 relative flex flex-col items-center justify-center"
        >
            {/* Glowing orb background effect */}
            <div className="absolute inset-0 z-0 overflow-hidden">
                <motion.div
                    className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50vmin] h-[50vmin] rounded-full bg-gradient-to-br from-accent/20 to-accent/5 blur-[10vmin]"
                    style={{ filter: `blur(${blurRadius.get()}px)` }}
                    animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.5, 0.7, 0.5]
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
            </div>

            {/* Section header */}
            <motion.div
                className="relative z-10 text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true, margin: "-100px" }}
            >
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-text-primary">
                    AI <span className="text-accent">Assistant</span>
                </h2>
                <p className="text-xl text-text-secondary max-w-2xl mx-auto">
                    Your personal crypto advisor powered by advanced intelligence
                </p>
            </motion.div>

            {/* Chat interface */}
            <motion.div
                className="relative z-10 w-full max-w-4xl"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
            >
                {/* Chat container */}
                <motion.div
                    className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100"
                    whileHover={{ boxShadow: "0 0 30px rgba(0, 112, 243, 0.2)" }}
                >
                    {/* Chat header */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center">
                                <span className="text-white text-xs font-bold">AI</span>
                            </div>
                            <div>
                                <h3 className="font-medium text-gray-800">CryptoVault Assistant</h3>
                                <div className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                    <span className="text-xs text-gray-500">Online</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <motion.button
                                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500"
                                whileHover={{ scale: 1.1, backgroundColor: "rgba(0, 0, 0, 0.05)" }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
                                </svg>
                            </motion.button>
                            <motion.button
                                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500"
                                whileHover={{ scale: 1.1, backgroundColor: "rgba(0, 0, 0, 0.05)" }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                </svg>
                            </motion.button>
                        </div>
                    </div>

                    {/* Messages area */}
                    <div
                        ref={messageContainerRef}
                        className="px-6 py-5 h-[350px] overflow-y-auto flex flex-col gap-4 bg-white"
                    >
                        {conversation.map((msg) => (
                            <motion.div
                                key={msg.id}
                                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                                variants={itemVariants}
                                initial="hidden"
                                animate="visible"
                                transition={{ duration: 0.3 }}
                            >
                                <div
                                    className={`max-w-[80%] p-4 rounded-2xl ${msg.sender === 'user'
                                        ? 'bg-accent/10 text-gray-800 rounded-tr-none'
                                        : 'bg-gray-100 text-gray-800 rounded-tl-none'
                                        }`}
                                >
                                    <p className="text-sm">{msg.message}</p>
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </motion.div>
                        ))}

                        {/* Typing indicator */}
                        {isTyping && (
                            <motion.div
                                className="flex justify-start"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <div className="max-w-[80%] p-4 rounded-2xl bg-gray-100 text-gray-800 rounded-tl-none">
                                    <p className="text-sm">{currentTypingMessage}</p>
                                    <div className="flex gap-1 mt-1">
                                        <motion.div
                                            className="w-1.5 h-1.5 rounded-full bg-accent"
                                            animate={{ y: [0, -5, 0] }}
                                            transition={{ duration: 0.5, repeat: Infinity, delay: 0 }}
                                        />
                                        <motion.div
                                            className="w-1.5 h-1.5 rounded-full bg-accent"
                                            animate={{ y: [0, -5, 0] }}
                                            transition={{ duration: 0.5, repeat: Infinity, delay: 0.1 }}
                                        />
                                        <motion.div
                                            className="w-1.5 h-1.5 rounded-full bg-accent"
                                            animate={{ y: [0, -5, 0] }}
                                            transition={{ duration: 0.5, repeat: Infinity, delay: 0.2 }}
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Suggested queries */}
                    <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex gap-2 overflow-x-auto">
                        {suggestedQueries.map((query, index) => (
                            <motion.button
                                key={index}
                                className="px-3 py-1.5 rounded-full bg-gray-100 text-xs whitespace-nowrap text-gray-700"
                                whileHover={{ backgroundColor: "rgba(0, 112, 243, 0.1)", scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleSuggestedQuery(query)}
                            >
                                {query}
                            </motion.button>
                        ))}
                    </div>

                    {/* Input area */}
                    <form onSubmit={handleSubmit} className="px-6 py-4 border-t border-gray-100 bg-white">
                        <div className="flex gap-2">
                            <motion.input
                                type="text"
                                value={currentMessage}
                                onChange={(e) => setCurrentMessage(e.target.value)}
                                placeholder="Ask anything about your crypto assets..."
                                className="flex-1 bg-gray-100 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 text-gray-800"
                                whileFocus={{ boxShadow: "0 0 0 2px rgba(0, 112, 243, 0.3)" }}
                            />
                            <motion.button
                                type="submit"
                                className="bg-accent hover:bg-accent-dark text-white rounded-full w-10 h-10 flex items-center justify-center"
                                whileHover={{ scale: 1.05, backgroundColor: "#0060d0" }}
                                whileTap={{ scale: 0.95 }}
                                disabled={!currentMessage.trim() || isTyping}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                                </svg>
                            </motion.button>
                        </div>
                    </form>
                </motion.div>

                {/* Features annotations */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        { icon: '🧠', title: 'Market intelligence', desc: 'Trained on real-time and historical crypto data' },
                        { icon: '📊', title: 'Portfolio analysis', desc: 'Personalized insights based on your holdings' },
                        { icon: '🔮', title: 'Predictive signals', desc: 'AI-powered trend forecasting and alerts' },
                    ].map((feature, i) => (
                        <motion.div
                            key={i}
                            className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-start gap-3"
                            variants={itemVariants}
                        >
                            <div className="text-2xl">{feature.icon}</div>
                            <div>
                                <h3 className="font-medium text-sm text-gray-800">{feature.title}</h3>
                                <p className="text-xs text-gray-500">{feature.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        </motion.section>
    );
};

export default AIAgentDemo; 